import {
  createContext,
  useContext,
  useEffect,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from "react";

export interface RouteOptions {
  hasUnsavedChanges: boolean;
}

export interface RouteOptionsMatch {
  pathname: string;
}

export type RouteOptionsByPath = Record<string, RouteOptions>;

interface RouteOptionsContextValue {
  optionsByPath: RouteOptionsByPath;
  setOptionsByPath: Dispatch<SetStateAction<RouteOptionsByPath>>;
}

const RouteOptionsContext = createContext<RouteOptionsContextValue | null>(
  null,
);

export function RouteOptionsProvider(props: PropsWithChildren) {
  const [optionsByPath, setOptionsByPath] = useState<RouteOptionsByPath>({});

  return (
    <RouteOptionsContext.Provider
      value={{
        optionsByPath: optionsByPath,
        setOptionsByPath: setOptionsByPath,
      }}
    >
      {props.children}
    </RouteOptionsContext.Provider>
  );
}

export function useRouteOptions(
  matchesRoute: (match: RouteOptionsMatch) => boolean,
) {
  const context = useRouteOptionsContext();
  return getMatchingRouteOptions(context.optionsByPath, matchesRoute);
}

export function useSetRouteOptions(routePath: string, options: RouteOptions) {
  const context = useRouteOptionsContext();
  const setOptionsByPath = context.setOptionsByPath;
  const hasUnsavedChanges = options.hasUnsavedChanges;

  useEffect(() => {
    setOptionsByPath((currentOptionsByPath) => {
      if (
        currentOptionsByPath[routePath]?.hasUnsavedChanges === hasUnsavedChanges
      ) {
        return currentOptionsByPath;
      }

      return {
        ...currentOptionsByPath,
        [routePath]: { hasUnsavedChanges: hasUnsavedChanges },
      };
    });

    return () => {
      setOptionsByPath((currentOptionsByPath) => {
        if (!(routePath in currentOptionsByPath)) {
          return currentOptionsByPath;
        }

        const nextOptionsByPath = { ...currentOptionsByPath };
        delete nextOptionsByPath[routePath];
        return nextOptionsByPath;
      });
    };
  }, [hasUnsavedChanges, routePath, setOptionsByPath]);
}

export function getMatchingRouteOptions(
  optionsByPath: RouteOptionsByPath,
  matchesRoute: (match: RouteOptionsMatch) => boolean,
): RouteOptions | undefined {
  const matchingOptions = Object.entries(optionsByPath)
    .filter(([pathname]) => {
      return matchesRoute({ pathname: pathname });
    })
    .map(([, options]) => {
      return options;
    });

  if (matchingOptions.length === 0) {
    return undefined;
  }

  return {
    hasUnsavedChanges: matchingOptions.some((options) => {
      return options.hasUnsavedChanges;
    }),
  };
}

function useRouteOptionsContext() {
  const context = useContext(RouteOptionsContext);

  if (!context) {
    throw new Error("Route options must be used within RouteOptionsProvider");
  }

  return context;
}
